import { REFRESH_TOKEN_STORAGE_NAME, TOKEN_STORAGE_NAME } from "@cinnamon/design-system/constants";
import { type PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getUserInfo, updateUserInfo } from "@services/auth";
import { getAllTeams } from "@services/team";
import { getAllUsers } from "@services/user";
import { jwtDecode } from "jwt-decode";

import logger from "@/helper/logger";
import { type UserLoginSuccess } from "@/types/auth";
import { type Team } from "@/types/team";
import { type UpdatedUserInput, type User } from "@/types/user";

export interface UserState {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user?: User;
  scope: string;
  users: User[];
  teams: Team[];
  tenantId: string | null;
}

export type SessionInfo = Pick<UserState, "user" | "scope" | "tenantId">;
export type UserInfo = Pick<UserState, "user">;

const initialState: UserState = {
  isInitialized: false,
  isAuthenticated: false,
  user: undefined,
  scope: "",
  users: [],
  teams: [],
  tenantId: null,
};

export const getSessionInfo = createAsyncThunk("user/getSessionInfo", async (_, thunkApi) => {
  try {
    const accessToken = `${localStorage.getItem(TOKEN_STORAGE_NAME)}`;
    const tokenParts = accessToken.split("Bearer ");
    const token = tokenParts.length > 1 ? tokenParts[1] : undefined;

    if (token) {
      const { scope, tenant: tenantId } = jwtDecode<{ scope: string; tenant: string }>(token) || {
        scope: "",
        tenant: null,
      };
      const user = await getUserInfo(accessToken);
      const sessionInfo = { user, scope, tenantId } as SessionInfo;
      if (sessionInfo) {
        thunkApi.dispatch(updateSession(sessionInfo));
      }
    }
  } catch (err) {
    logger.error(JSON.stringify(err), { data: { context: "getSessionInfo" } });
  } finally {
    // Turn off loader and init page
    thunkApi.dispatch(initSession());
  }
});

export const onGetInfoLogin = createAsyncThunk(
  "user/onGetInfoLogin",
  async ({ access, token_type: tokenType, refresh }: UserLoginSuccess, { rejectWithValue }) => {
    try {
      const token = access;
      const accessToken = `${tokenType || "Bearer"} ${token}`;
      const refreshToken = refresh;
      let scope = "";
      let tenantId = null;
      if (token) {
        scope = jwtDecode<{ scope: string }>(token).scope || "";
        tenantId = jwtDecode<{ tenant: string }>(token).tenant || "";
      }
      localStorage.setItem(TOKEN_STORAGE_NAME, accessToken);
      localStorage.setItem(REFRESH_TOKEN_STORAGE_NAME, refreshToken);

      const user = await getUserInfo(accessToken);
      return { user, scope, tenantId };
    } catch (err) {
      return rejectWithValue(JSON.stringify(err));
    }
  }
);

export const getUsers = createAsyncThunk("user/getUsers", async () => {
  const users = await getAllUsers();
  return { users };
});

export const getTeams = createAsyncThunk("user/getTeams", async () => {
  const teams = await getAllTeams();
  return { teams };
});

export const updateUserInfoAsyncThunk = createAsyncThunk(
  "user/updateUserInfoAsyncThunk",
  async (userInfo: UpdatedUserInput, { rejectWithValue }) => {
    try {
      const user = await updateUserInfo(userInfo);
      return { user };
    } catch (err) {
      return rejectWithValue(JSON.stringify(err));
    }
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    initSession: (state) => {
      state.isInitialized = true;
    },
    updateSession: (state, action) => {
      const { payload } = action as PayloadAction<SessionInfo>;
      state.user = payload.user;
      state.isAuthenticated = true;
      state.scope = payload.scope;
      state.tenantId = payload.tenantId;
    },
    clearSession: (state) => {
      state.user = undefined;
      state.isAuthenticated = false;
      state.scope = "";
      state.tenantId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(onGetInfoLogin.fulfilled, (state, action) => {
      const payload = action.payload as { user: User; scope: string; tenantId: string | null };
      state.user = payload.user;
      state.isAuthenticated = true;
      state.scope = payload.scope;
      state.tenantId = payload.tenantId;
    });
    builder.addCase(onGetInfoLogin.rejected, () => {
      localStorage.removeItem(TOKEN_STORAGE_NAME);
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_NAME);
    });
    builder.addCase(updateUserInfoAsyncThunk.fulfilled, (state, action: PayloadAction<UserInfo>) => {
      state.user = action.payload?.user;
    });
    builder.addCase(getUsers.fulfilled, (state, action: PayloadAction<{ users: User[] }>) => {
      state.users = action.payload.users;
    });
    builder.addCase(getTeams.fulfilled, (state, action: PayloadAction<{ teams: Team[] }>) => {
      state.teams = action.payload.teams;
    });
  },
});

export const { updateSession, clearSession, initSession } = userSlice.actions;

export default userSlice.reducer;
